import forEach from 'lodash/forEach'
import words from 'lodash/words'

const VD_BASE_CLASS = 'vd-avatar'
const VD_INITIALS_CLASS = `${VD_BASE_CLASS}--initials`
const DEFAULT_IMAGE_SIZE = 'sm'
const NO_AVATAR_TEST = /\/no-image-white-ss\.png$/
const DELETED_IMAGE_CLASS = `${VD_BASE_CLASS}--deleted-image`

/**
 * Controller for the vd-avatar component.
 *
 * @class AvatarController
 */
export default class AvatarController {
  constructor($attrs, $element, elementService) {
    'ngInject'
    this._$element = $element
    this._$attrs = $attrs
    this._elementService = elementService
    this.isInDeletedState = false
  }

  $onChanges() {
    this._setAvatarType()
  }

  $postLink() {
    const elementClasses = [VD_BASE_CLASS]
    const modifiers =
      this._elementService.getComponentAttrs(this._$attrs, 'modifier') || []

    if (modifiers) {
      forEach(
        modifiers,
        modifier =>
          modifier && elementClasses.push(`${VD_BASE_CLASS}--${modifier}`)
      )
    }

    this._$attrs.$addClass(elementClasses)
    this._setAvatarType()
  }

  /**
   * Determines the appropriate avatar type and sets the required content.
   *
   * @private
   * @method _setAvatarType
   */
  _setAvatarType() {
    if (this.entity.time_until_deletion) {
      this._clearInitials()
      this._clearInitialsModifier()
      this._setDeletedAvatarState()
      return
    }

    this._removeDeletedAvatarState()

    if (this._hasImage(this.entity)) {
      this._clearInitials()
      this._clearInitialsModifier()
      this._setBackgroundImage()
    } else {
      this._clearBackgroundImage()
      this._setInitials()
      this._setInitialsModifier()
    }
  }

  /**
   * Sets the background image for the avatar.
   *
   * @private
   * @method _setBackgroundImage
   */
  _setBackgroundImage() {
    const bgImage = this.entity.images[DEFAULT_IMAGE_SIZE]

    if (this.backgroundImage === bgImage) {
      return
    }

    this._$element.css('background-image', `url(${bgImage})`)
    this.backgroundImage = bgImage
  }

  /**
   * Sets the avatar to a deleted state.
   *
   * @private
   * @method _setDeletedAvatarState
   */
  _setDeletedAvatarState() {
    if (!this.isInDeletedState) {
      this.isInDeletedState = true
      this._$element.addClass(DELETED_IMAGE_CLASS)
    }
  }

  /**
   * Removes deleted state from the avatar.
   *
   * @private
   * @method _removeDeletedAvatarState
   */
  _removeDeletedAvatarState() {
    if (this.isInDeletedState) {
      this.isInDeletedState = false
      this._$element.removeClass(DELETED_IMAGE_CLASS)
    }
  }

  /**
   * Clears the background image currently set for the avatar.
   *
   * @private
   * @method _clearBackgroundImage
   */
  _clearBackgroundImage() {
    if (this.backgroundImage) {
      this._$element.css('background-image', '')
      this.backgroundImage = null
    }
  }

  /**
   * Sets the initials for the avatar.
   *
   * @private
   * @method _setInitials
   */
  _setInitials() {
    const initials = this._getInitials(this.entity)

    if (this.initials === initials) {
      return
    }

    this.initials = initials
  }

  /**
   * Sets the initials modifier for the avatar.
   *
   * @private
   * @method _setInitialsModifier
   */
  _setInitialsModifier() {
    const initialsModifier = `${VD_INITIALS_CLASS} ${VD_INITIALS_CLASS}-${this._getColourNumber(
      this.entity.id
    )}`

    if (this.initialsModifier === initialsModifier) {
      return
    }

    this._clearInitialsModifier()
    this.initialsModifier = initialsModifier
    this._$attrs.$addClass(this.initialsModifier)
  }

  /**
   * Clear the initials and initials modifier currently set for the avatar.
   *
   * @private
   * @method _clearInitials
   */
  _clearInitials() {
    if (this.initials) {
      this.initials = null
    }
  }

  /**
   * Responsible for clearing the initials modifier.
   *
   * @private
   * @method _clearInitialsModifier
   */
  _clearInitialsModifier() {
    if (this.initialsModifier) {
      this._$attrs.$removeClass(this.initialsModifier)
      this.initialsModifier = null
    }
  }

  /**
   * Determine if the entity has an image that can be used.
   *
   * @private
   * @method _hasImage
   *
   * @return {Boolean} True if the entity has an image that can be shown, false otherwise.
   */
  _hasImage(entity) {
    if (!entity) {
      return false
    }

    const entityImage = entity.images && entity.images[DEFAULT_IMAGE_SIZE]
    // Don't show a background image if a placeholder image is present
    const placeHolderImage =
      entity.image_source && entity.image_source.match(NO_AVATAR_TEST)

    return Boolean(entityImage && !placeHolderImage)
  }

  /**
   * Get the initials given an entity.
   *
   * @private
   * @param  {Object} entity
   * @return {String}
   */
  _getInitials(entity) {
    const maxInitials = 2
    const lookupFields = ['display_name', 'username', 'company_name', 'name']
    let initialsField
    let initials = ''

    // iterate through lookupFields until we find a non empty one.
    forEach(lookupFields, field => {
      if (entity.hasOwnProperty(field) && entity[field]) {
        initialsField = field
        return false
      }
    })

    if (!initialsField) {
      return initials
    }

    forEach(words(entity[initialsField], /[^, ]+/g), word => {
      if (initials.length >= maxInitials) {
        return false
      }

      initials += word[0].toUpperCase()
    })

    return initials
  }

  /**
   * Gets a colour number given an ID.
   *
   * @private
   * @param  {String} id
   * @return {Number}
   */
  _getColourNumber(id) {
    const hash = Math.abs(this._getHash(id))
    const totalColours = 7

    return (hash % totalColours) + 1
  }

  /**
   * Returns a hash given a string seed.
   *
   * @private
   * @param  {String} seed
   * @return {Number}
   */
  _getHash(seed) {
    let hash = 0

    if (!seed || seed.length === 0) {
      return hash
    }

    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash |= 0
    }

    return hash
  }
}
